import cx from 'classnames'
import { ReactNodeViewPortalsProvider } from 'common/Prose/nodeViews'
import { useEditorFonts } from 'hooks/useEditorFonts/useEditorFonts'
import { useFadingScrollbar } from 'hooks/useFadingScrollbar'
import { Fragment, MarkType, Node, Schema } from 'prosemirror-model'
import { EditorState, Plugin, TextSelection } from 'prosemirror-state'
import { Step, Transform } from 'prosemirror-transform'
import { Decoration, EditorView, NodeView } from 'prosemirror-view'
import { buildArticleStyleCSSProperties } from 'proseShared/utils'
import { useEffect, useRef, useState } from 'react'
import { nerudaNodeViews } from 'screens/ProseEdit/components/NerudaDocEditor/nerudaNodeViews'
import { ArticleStyle, Layer } from 'types'
import { useReactNodeViewPortals } from '../Prose/nodeViews/ReactNodeView/ReactnodeViewPortalsContext'
import Styles from './ProsePreview.module.scss'
import { useLayer } from 'hooks/queries/layers/useLayer'
import { AuthStore, AuthStoreContext } from 'stores/AuthStore'
import { useRootStore } from 'stores/RootStore'
import { useAuth } from 'hooks/UseAuth'
import { getDocumentFromJson } from 'proseShared/utils/state'

interface Props {
  articleStyles?: (ArticleStyle | null | undefined)[] // later article styles take precedence
  className?: string
  nodes: Node[] | Fragment
  schema: Schema
  /*
   * A callback to run after the prosemirror view is initialised.
   * This callback can be used to:
   * - scroll to a certain point in the document
   * - trigger side effects
   */
  onInit?: (view: EditorView, container: ParentNode) => void

  /*
   * List of plugins to enable in the prose preview.
   */
  plugins?: Plugin[]
  removeMarks?: MarkType[]
  nodeViews?: {
    [node: string]: (
      node: Node<any>,
      view: EditorView<any>,
      getPos: () => number,
      decorations: Decoration[],
    ) => NodeView
  }
}

const Editor = ({
  articleStyles,
  className,
  nodes,
  onInit,
  plugins,
  removeMarks,
  schema,
  nodeViews: propNodeViews = {},
}: Props) => {
  const ref = useRef<HTMLDivElement>(null)
  const { createPortal, removePortal } = useReactNodeViewPortals()
  useFadingScrollbar(ref.current)
  useEditorFonts()

  const getEditorState = (doc: Node) => {
    return EditorState.create({
      doc,
      plugins,
      selection: TextSelection.create(doc, 0, 0), // Make sure nothing is selected, in case the first node is non-text (e.g. image).
    })
  }

  useEffect(() => {
    if (!ref.current || !schema) return

    const doc = schema.nodes.doc.create({}, nodes)

    const transform = new Transform(doc)
    removeMarks?.forEach((mark) => transform.removeMark(0, doc.nodeSize - 2, mark))

    const state = getEditorState(transform.doc)

    const nodeViews = nerudaNodeViews({
      readOnly: true,
      createPortal,
      removePortal,
    })

    const editorView = new EditorView(ref.current, {
      nodeViews: { ...nodeViews, ...propNodeViews },
      state,
      editable: () => false,
      dispatchTransaction: function (originalTr) {
        const updatedState = this.state.apply(originalTr)
        // check if view is mounted, otherwise it might try to update an unmounted view   s
        // causing jest to bark
        if ((this as any).docView) {
          ;(this as any).updateState(updatedState)
        }
      },
    })

    onInit?.(editorView, ref.current)

    return () => editorView.destroy()

    // NOTE: Since nodes are complex objects with functions, we resort to a
    // primitive way of detecting changes in the dependencies
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(nodes)])

  return (
    <div
      className={cx(className, Styles.editorRenderer, 'prosemirror-preview', Styles.scope)}
      ref={ref}
      style={articleStyles && buildArticleStyleCSSProperties(...articleStyles)}
    />
  )
}

export const PreviewWithLayerSnapshots = ({ layer, ...previewProps }: Omit<Props, 'nodes'> & { layer: Layer }) => {
  const { schema } = previewProps
  const rootStore = useRootStore()
  const auth = useAuth()
  const [authStore] = useState(() => new AuthStore(rootStore, auth))
  const [nodes, setNodes] = useState<Fragment>(Fragment.empty)

  useLayer(
    {
      articleKey: layer.article_id,
      id: layer.id,
    },
    {
      onSuccess: (data) => {
        if (data?.layer && data.layer.last_snapshot_delta_steps) {
          const initElements = data?.layer.elements
          const documentFromJson = getDocumentFromJson(initElements ?? [], schema)
          const snapshotState = EditorState.create({ doc: documentFromJson })
          const { tr } = snapshotState
          data.layer.last_snapshot_delta_steps.forEach((stepRow) => {
            const step = Step.fromJSON(schema, stepRow.data)
            tr.step(step)
          })
          const newState = snapshotState.apply(tr)
          setNodes(newState.doc.content)
        }
      },
    },
  )

  return (
    <AuthStoreContext.Provider value={authStore}>
      <ProsePreview {...previewProps} nodes={nodes} />
    </AuthStoreContext.Provider>
  )
}

export const ProsePreview = (props: Props) => {
  return (
    <ReactNodeViewPortalsProvider>
      <Editor {...props} />
    </ReactNodeViewPortalsProvider>
  )
}
